import { TwoFactorResponse } from "@sp-crm/core";
import { LoginWorkflowLayout } from "components/app/login-workflow-layout";
import { SupportEmailLink } from "components/app/support-email-link";
import { InlineBanner } from "components/ui/inline-banner";
import { Input } from "components/ui/input";
import { PrimaryButton } from "components/ui/primary-button";
import { Spinner } from "components/ui/spinner";
import * as React from "react";
import { connect } from "react-redux";
import { Link, RouteComponentProps, withRouter } from "react-router-dom";
import { Action, Dispatch } from "redux";
import { ApplicationState } from "store/state";
import {
    clearPasswordReset2FA,
    sendPasswordResetVerificationCode,
    setPassword,
    verifyPasswordResetCode,
} from "../../store/actions/password-reset";
import { IResetPasswordState } from "../../store/reducers/reset-password";
import { ISetPasswordState } from "../../store/reducers/set-password";
import * as http from "../../util/http";
import { PasswordReset2FAInline } from "./password-reset-2fa-inline";

interface ResetPasswordEmailPropsFromDispatch {
    doSetPassword: (password: string, token: string) => void;
    sendVerificationCode: (method: string) => Promise<void>;
    verifyCode: (code: string) => Promise<void>;
    clearPasswordReset2FA: () => void;
}

interface ResetPasswordEmailPropsFromState {
    setPassword: ISetPasswordState;
    resetPassword: IResetPasswordState;
    token: string;
}

interface ResetPasswordEmailState {
    password: string;
    passwordAgain: string;
    formIsSubmitted: boolean;
    errors: IResetPasswordErrors;
    tokenState: "loading" | "invalid" | "valid";
    twoFactorData: TwoFactorResponse | null;
    verificationError: string | null;
    twoFactorCompleted: boolean;
}

interface IResetPasswordErrors {
    passwordNotValid: boolean;
    passwordAgainNotValid: boolean;
    notMatching: boolean;
}

type ResetPasswordEmailProps = ResetPasswordEmailPropsFromState &
    ResetPasswordEmailPropsFromDispatch;

class ResetPasswordEmail extends React.Component<
    ResetPasswordEmailProps,
    ResetPasswordEmailState
> {
    constructor(props: ResetPasswordEmailProps) {
        super(props);
        this.state = {
            password: "",
            passwordAgain: "",
            formIsSubmitted: false,
            errors: {
                passwordNotValid: true,
                passwordAgainNotValid: true,
                notMatching: true,
            },
            tokenState: "loading",
            twoFactorData: null,
            verificationError: null,
            twoFactorCompleted: false,
        };
    }

    override async componentDidMount() {
        try {
            const response = await http.post<TwoFactorResponse>(
                http.newRequestDescription("/api/password/validate-token"),
                `/api/password/validate-token`,
                { token: this.props.token },
            );

            this.setState({
                tokenState: "valid",
                twoFactorData: response.data.requiresTwoFactor ? response.data : null,
                twoFactorCompleted: !response.data.requiresTwoFactor,
            });
        } catch (error) {
            this.setState({ tokenState: "invalid" });
        }
    }

    isFormValid(errors: IResetPasswordErrors): boolean {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- eslintintroduction
        return Object.keys(errors).every((error: any) => !(errors as any)[error]);
    }

    isPasswordValid(password: string): boolean {
        return Boolean(password && password.length >= 5);
    }

    arePasswordsMatching(password: string, passwordAgain: string): boolean {
        return password !== passwordAgain;
    }

    onPasswordChange(newPass: string, passwordKey: "password" | "passwordAgain") {
        const oppositePasswordKey =
            passwordKey === "password" ? "passwordAgain" : "password";
        const passwordNotValid =
            passwordKey === "password"
                ? !this.isPasswordValid(newPass)
                : this.state.errors.passwordNotValid;
        const passwordAgainNotValid =
            passwordKey === "password"
                ? this.state.errors.passwordAgainNotValid
                : !this.isPasswordValid(newPass);

        this.setState({
            ...this.state,
            [passwordKey]: newPass,
            errors: {
                passwordNotValid: passwordNotValid,
                passwordAgainNotValid: passwordAgainNotValid,
                notMatching: this.arePasswordsMatching(
                    newPass,
                    this.state[oppositePasswordKey],
                ),
            },
        });
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- eslintintroduction
    onSubmit(e: any) {
        e.preventDefault();
        this.setState({ ...this.state, formIsSubmitted: true });

        if (!this.isFormValid(this.state.errors)) {
            return;
        }
        this.props.doSetPassword(this.state.password, this.props.token);
    }

    handleTwoFactorComplete = () => {
        this.setState({ twoFactorCompleted: true });
    };

    override componentDidUpdate(prevProps: ResetPasswordEmailProps) {
        if (!prevProps.setPassword.isSuccessful && this.props.setPassword.isSuccessful) {
            setTimeout(() => {
                window.location.href = "/login?passwordReset=success";
            }, 1500);
        }
    }

    override render() {
        const { isSuccessful, isFailed } = this.props.setPassword;
        const { tokenState, twoFactorData, verificationError } = this.state;

        if (tokenState === "loading") {
            return (
                <LoginWorkflowLayout title="Reset your password">
                    <div className="flex justify-center">
                        <Spinner />
                    </div>
                </LoginWorkflowLayout>
            );
        }

        if (tokenState === "invalid") {
            return (
                <LoginWorkflowLayout title="Reset your password">
                    <InlineBanner type="error">
                        This password reset link is invalid or has expired. Please{" "}
                        <Link to="/account/reset-password">request a new one</Link>.
                    </InlineBanner>
                </LoginWorkflowLayout>
            );
        }

        if (twoFactorData && !this.state.twoFactorCompleted) {
            return (
                <LoginWorkflowLayout title="Reset your password">
                    <PasswordReset2FAInline
                        twoFactorData={twoFactorData}
                        onComplete={this.handleTwoFactorComplete}
                        errorMessage={verificationError}
                        token={this.props.token}
                    />
                </LoginWorkflowLayout>
            );
        }

        return (
            <LoginWorkflowLayout title="Reset your password">
                <div className="space-y-6">
                    {isFailed && (
                        <InlineBanner type="error">
                            Something went wrong. Please contact <SupportEmailLink /> for
                            further information
                        </InlineBanner>
                    )}
                    {isSuccessful && (
                        <InlineBanner type="success">
                            Password has been successfully reset. You will be redirected
                            to the login page shortly.
                        </InlineBanner>
                    )}
                    <form
                        onSubmit={this.onSubmit.bind(this)}
                        noValidate
                        className="space-y-6">
                        <div>
                            <Input
                                type="password"
                                value={this.state.password}
                                onChange={e =>
                                    this.onPasswordChange(e.target.value, "password")
                                }
                                label="New password"
                            />
                        </div>

                        <div>
                            <Input
                                type="password"
                                value={this.state.passwordAgain}
                                onChange={e =>
                                    this.onPasswordChange(e.target.value, "passwordAgain")
                                }
                                label="New password (again)"
                            />
                        </div>

                        {this.state.formIsSubmitted && (
                            <>
                                {this.state.errors.passwordNotValid && (
                                    <InlineBanner type="error">
                                        Password should contain at least 5 characters
                                    </InlineBanner>
                                )}
                                {!this.state.errors.passwordNotValid &&
                                    this.state.errors.passwordAgainNotValid && (
                                        <InlineBanner type="error">
                                            Password should contain at least 5 characters
                                        </InlineBanner>
                                    )}
                                {!this.state.errors.passwordNotValid &&
                                    !this.state.errors.passwordAgainNotValid &&
                                    this.state.errors.notMatching && (
                                        <InlineBanner type="error">
                                            Passwords do not match
                                        </InlineBanner>
                                    )}
                            </>
                        )}

                        <PrimaryButton type="submit" className="w-full">
                            Reset password
                        </PrimaryButton>
                    </form>
                </div>
            </LoginWorkflowLayout>
        );
    }
}

function mapStateToProps(
    state: ApplicationState,
    ownProps: RouteComponentProps,
): ResetPasswordEmailPropsFromState {
    const queryParams = new URLSearchParams(ownProps.location.search);
    const token = queryParams.get("token") || "ERR_MISSING";

    return {
        setPassword: state.setPassword,
        resetPassword: state.resetPassword,
        token,
    };
}

function mapDispatchToProps(
    dispatch: Dispatch<Action>,
): ResetPasswordEmailPropsFromDispatch {
    return {
        doSetPassword: (password: string, token: string) =>
            setPassword(password, token, dispatch),
        sendVerificationCode: (method: string) =>
            sendPasswordResetVerificationCode(method, dispatch),
        verifyCode: (code: string) => verifyPasswordResetCode(code, dispatch),
        clearPasswordReset2FA: () => clearPasswordReset2FA(dispatch),
    };
}

export default withRouter(
    connect(mapStateToProps, mapDispatchToProps)(ResetPasswordEmail),
);
